import { useEffect } from 'react';

import { api } from '@/lib/api';

/**
 * How often the screen says it is still there, in milliseconds.
 *
 * Warning: shorter than the server's tolerance (`HEARTBEAT_TTL_MS`, five
 * minutes), or a late beat would drop somebody offline between two of them.
 */
const BEAT_MS = 2 * 60 * 1000;

/**
 * Tells the server the reader is around.
 *
 * Warning: presence is fed by the `/presence` WebSocket, and **this client
 * never subscribes to it** — so before this, everybody read as offline,
 * including whoever was looking at the screen. A small request every two
 * minutes buys the same answer without the socket, the dependency, or the
 * kilobytes it costs the budget.
 *
 * Warning: only while the tab is **visible**. A window left open for three
 * days is not somebody available to play, and a background tab beating all
 * night would say it is.
 *
 * @see docs/features/messaging.md
 */
export function usePresenceHeartbeat(): void {
  useEffect(() => {
    // Failure is silent on purpose: nothing on the screen depends on it, and
    // an error toast for "we could not say hello" is noise.
    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      void api.presence.heartbeat().catch(() => undefined);
    };

    beat();
    const timer = setInterval(beat, BEAT_MS);
    // Coming back to the tab is worth a beat of its own: the last one may be
    // hours old, and the reader is here now.
    document.addEventListener('visibilitychange', beat);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', beat);
    };
  }, []);
}
